import { Capacitor } from '@capacitor/core';

/**
 * Native-only fix for the iOS Auth0 `missing_transaction` ("Invalid state")
 * login failure.
 *
 * WHY THIS EXISTS
 * ---------------
 * auth0-spa-js stores the in-flight login transaction (the PKCE `code_verifier`,
 * `state` and `nonce`) in `window.sessionStorage` by default, or in a cookie
 * when `useCookiesForTransactions` is set. On the Capacitor iOS shell BOTH of
 * those are lost across the login round-trip:
 *
 *   * The WKWebView origin is `capacitor://app.rettx.eu` — iOS refuses to let
 *     Capacitor register the reserved `https` scheme, so `iosScheme: 'https'`
 *     silently falls back to `capacitor://` (unlike Android, which really does
 *     serve from `https://`). `sessionStorage` on that origin lives only in the
 *     WebView's in-memory session, which iOS tears down while the system browser
 *     (SFSafariViewController) is foregrounded for the Auth0 login. By the time
 *     the deep link re-enters the app and `handleRedirectCallback` runs, the
 *     transaction is GONE → spa-js throws `missing_transaction`.
 *   * The cookie fallback doesn't help either: spa-js only marks the transaction
 *     cookie `Secure; SameSite=None` when `location.protocol === 'https:'`, and
 *     WKWebView does not reliably persist a plain `document.cookie` written on a
 *     custom (`capacitor://`) scheme across the same round-trip.
 *
 * `localStorage`, by contrast, DOES persist on the `capacitor://` origin — it is
 * the same store the Auth0 token cache already relies on (`cacheLocation:
 * 'localstorage'`), which is why staying signed in works. So the reliable fix is
 * to make spa-js's transaction reads/writes land in `localStorage`.
 *
 * spa-js's `SessionStorage` wrapper talks to the global `window.sessionStorage`
 * directly and exposes no hook to swap the transaction store, so we back
 * `window.sessionStorage` itself with `localStorage` on native (iOS and
 * Android). Application code also stores the short-lived stale-chunk recovery
 * timestamp here. Every key is namespaced in the backing store, so it cannot
 * collide with normal application localStorage entries. Web/PWA is untouched.
 */

const PREFIX = '__cap_ss__:';

/**
 * A minimal, spec-complete `Storage` implementation persisted in `localStorage`
 * under a private prefix so it never collides with the app's own keys.
 */
class LocalStorageBackedSessionStorage implements Storage {
  private namespacedKeys(): string[] {
    const keys: string[] = [];
    for (let i = 0; i < localStorage.length; i++) {
      const k = localStorage.key(i);
      if (k !== null && k.startsWith(PREFIX)) {
        keys.push(k.slice(PREFIX.length));
      }
    }
    return keys;
  }

  get length(): number {
    return this.namespacedKeys().length;
  }

  clear(): void {
    this.namespacedKeys().forEach((k) => localStorage.removeItem(PREFIX + k));
  }

  getItem(key: string): string | null {
    return localStorage.getItem(PREFIX + key);
  }

  key(index: number): string | null {
    return this.namespacedKeys()[index] ?? null;
  }

  removeItem(key: string): void {
    localStorage.removeItem(PREFIX + key);
  }

  setItem(key: string, value: string): void {
    localStorage.setItem(PREFIX + key, value);
  }
}

/**
 * On native only, replace `window.sessionStorage` with a `localStorage`-backed
 * store so the Auth0 login transaction survives the iOS SFSafariViewController
 * round-trip. Idempotent and defensive: any failure (e.g. a locked-down
 * `localStorage`) leaves the real `sessionStorage` in place rather than breaking
 * bootstrap. No-op on web/PWA.
 *
 * Must run BEFORE the Auth0 client is constructed / any login is started, so it
 * is invoked at the very top of `main.ts`.
 */
export function installNativeTransactionStorage(): void {
  if (!Capacitor.isNativePlatform()) {
    return;
  }
  try {
    // Verify localStorage is usable before we route session data into it.
    const probe = `${PREFIX}__probe__`;
    localStorage.setItem(probe, '1');
    localStorage.removeItem(probe);

    Object.defineProperty(window, 'sessionStorage', {
      configurable: true,
      writable: true,
      value: new LocalStorageBackedSessionStorage(),
    });
  } catch {
    // Leave the platform sessionStorage untouched if we can't install the shim.
  }
}
